import { z } from "zod";

/** Safe mobile wire types for named, version-bound document approvals. */
export const DocumentApprovalRequestStatusSchema = z.enum([
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
]);

export const DocumentApprovalGateStatusSchema = z.enum([
  "NONE",
  "PENDING",
  "REJECTED",
  "APPROVED",
]);

export const DocumentApprovalSummarySchema = z.object({
  requestId: z.string(),
  documentId: z.string(),
  versionId: z.string(),
  status: DocumentApprovalRequestStatusSchema,
  approverUserId: z.string(),
  approverName: z.string(),
  requestedByUserId: z.string(),
  requestedByName: z.string(),
  requestedAt: z.string().datetime(),
  requestNote: z.string().nullable(),
  decidedAt: z.string().datetime().nullable(),
  decisionReason: z.string().nullable(),
}).strict();
export type DocumentApprovalSummary = z.infer<typeof DocumentApprovalSummarySchema>;

export const PendingApprovalRowSchema = z.object({
  requestId: z.string(),
  documentId: z.string(),
  documentType: z.string(),
  documentTypeLabel: z.string(),
  versionId: z.string(),
  versionNumber: z.number().int(),
  projectId: z.string(),
  projectTitle: z.string(),
  requestedByName: z.string(),
  requestedAt: z.string().datetime(),
  requestNote: z.string().nullable(),
  supersededByVersionNumber: z.number().int().nullable(),
}).strict();
export type PendingApprovalRowWire = z.infer<typeof PendingApprovalRowSchema>;

export const EligibleDocumentApproverSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  role: z.string(),
}).strict();

export const DocumentApprovalStateSchema = z.object({
  enabled: z.boolean(),
  status: DocumentApprovalGateStatusSchema,
  request: DocumentApprovalSummarySchema.nullable(),
  targetVersionId: z.string().nullable(),
  latestVersionId: z.string().nullable(),
  canRequest: z.boolean(),
  canDecide: z.boolean(),
  eligibleApprovers: z.array(EligibleDocumentApproverSchema),
  defaultApproverUserId: z.string().nullable(),
}).strict();
export type DocumentApprovalState = z.infer<typeof DocumentApprovalStateSchema>;

export const DocumentApprovalReviewSchema = z.object({
  request: DocumentApprovalSummarySchema,
  document: z.object({
    documentId: z.string(),
    documentType: z.string(),
    documentTypeLabel: z.string(),
    projectId: z.string(),
    projectTitle: z.string(),
  }).strict(),
  assignedVersion: z.object({
    versionId: z.string(),
    versionNumber: z.number().int(),
    createdAt: z.string().datetime(),
    supersededByVersionNumber: z.number().int().nullable(),
  }).strict(),
  canDecide: z.boolean(),
}).strict();
export type DocumentApprovalReview = z.infer<typeof DocumentApprovalReviewSchema>;

export const PendingApprovalsResponseSchema = z.object({
  items: z.array(PendingApprovalRowSchema),
}).strict();

export const DocumentApprovalPdfResponseSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string().datetime(),
  versionId: z.string(),
  versionNumber: z.number().int(),
}).strict();

export const RequestDocumentApprovalInputSchema = z.object({
  versionId: z.string().min(1),
  approverUserId: z.string().min(1).optional(),
  note: z.string().max(2000).nullable().optional(),
}).strict();

export const DecideDocumentApprovalInputSchema = z.object({
  requestId: z.string().min(1),
  decision: z.enum(["APPROVED", "REJECTED"]),
  reason: z.string().max(2000).nullable().optional(),
}).strict().refine(
  (body) => body.decision !== "REJECTED" || Boolean(body.reason?.trim()),
  { message: "A reason is required when rejecting a version", path: ["reason"] },
);
